import { Button, Flex, Text, useMantineTheme } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { AcceptanceStatus } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import {
	DecisionCardBox,
	DecisionRow,
	RejectionCommentBox,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/Review/styled';

interface Props {
	status: AcceptanceStatus;
	auditedBy?: string;
	timestamp?: string;
	rejectionComment?: string | null;
	onUpdateDecision: () => void;
	isLoading?: boolean;
	disabled?: boolean;
}

export const DecisionCard = ({
	status,
	auditedBy,
	timestamp,
	rejectionComment,
	onUpdateDecision,
	isLoading,
	disabled,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const theme = useMantineTheme();
	const isApproved = status === AcceptanceStatus.APPROVED;
	const isRejected =
		status === AcceptanceStatus.REJECTED || status === AcceptanceStatus.REREQUESTED;

	if (!isApproved && !isRejected) {
		return null;
	}

	return (
		<Flex direction="column" gap="0.75rem">
			<DecisionCardBox $variant={isApproved ? 'approved' : 'rejected'}>
				<Flex align="center" gap="0.5rem">
					<Icon
						variant={isApproved ? 'tick-circle' : 'c-remove'}
						size="xl"
						fillStroke
						color={isApproved ? theme.colors.teal[8] : theme.colors.red[6]}
					/>
					<Text size="B5Bold">
						{isApproved
							? tMipManage('kyc.review.decision_card.approved')
							: tMipManage('kyc.review.decision_card.rejected')}
					</Text>
				</Flex>
				<DecisionRow>
					<Text size="B5" c="dark.0">
						{tMipManage('kyc.review.decision_card.audited_by')}
					</Text>
					<Text size="B5Bold">{auditedBy ?? '—'}</Text>
				</DecisionRow>
				<DecisionRow>
					<Text size="B5" c="dark.0">
						{tMipManage('kyc.review.decision_card.timestamp')}
					</Text>
					<Text size="B5">{timestamp ?? '—'}</Text>
				</DecisionRow>
				{isRejected && rejectionComment && (
					<RejectionCommentBox>
						<Icon variant="message-box" size="sm" />
						<Text size="B5">{rejectionComment}</Text>
					</RejectionCommentBox>
				)}
			</DecisionCardBox>
			<Button
				data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_KYC_REVIEW_UPDATE_DECISION_BUTTON}
				variant="default"
				fullWidth
				onClick={onUpdateDecision}
				loading={isLoading}
				disabled={disabled}
				leftSection={<Icon variant="refresh" size="xl" />}
			>
				{tMipManage('kyc.review.decision_card.update_decision')}
			</Button>
		</Flex>
	);
};
